import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ReviewModal from '../layouts/ReviewModal';
import NoteReview from './NoteReview';
import MultiPatientQueueReview from './MultiPatientQueueReview';
import { useSession } from '../contexts/SessionContext';
import { patientPath, ROUTES } from '../constants/routes';
import { isMultiPatientHandover } from '../utils/handoverUtils';
import { mergeReviewQueueToMultiPatientDraft } from '../utils/mergeReviewQueue';
import { discardClinicalNote } from '../services/persistClinical';
import { useLanguage } from '../contexts/LanguageContext';

function isMultiPatientHandoverDraft(data) {
  const handover = data?.generatedNotes?.find((entry) => entry.type === 'handover' && entry.note)?.note;
  return handover ? isMultiPatientHandover(handover) : false;
}

export default function NoteReviewScreen() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { draftGeneration, setDraftGeneration, saveNote, saveCarePlan, admittedPatients } =
    useSession();
  const { language } = useLanguage();

  const data = location.state || draftGeneration;
  const initialQueue = data?.reviewQueue || null;

  const mergedMultiPatient = useMemo(() => {
    if (!initialQueue || initialQueue.length < 2) return null;
    return mergeReviewQueueToMultiPatientDraft(initialQueue, admittedPatients, language);
  }, [initialQueue, admittedPatients, language]);

  // Typed multi-patient queue (vitals/MAR/etc.) — one-page avatar UI, keep note types
  const typedMultiQueue =
    !mergedMultiPatient && Array.isArray(initialQueue) && initialQueue.length >= 2
      ? initialQueue
      : null;

  const reviewPayload = mergedMultiPatient || data;
  const resolvedPatientId = id || reviewPayload?.patientId || null;
  const isMulti =
    isMultiPatientHandoverDraft(reviewPayload) || Boolean(typedMultiQueue);

  useEffect(() => {
    if (
      !data?.noteType
      || id
      || resolvedPatientId
      || isMultiPatientHandoverDraft(data)
      || mergedMultiPatient
      || typedMultiQueue
    ) {
      return;
    }
    navigate(ROUTES.PATIENT_SELECT, {
      replace: true,
      state: {
        generationResult: data,
        patientMatch: data.patientMatch,
        patientHints: data.patientHints,
        reviewQueue: data.reviewQueue,
        reviewQueueIndex: data.reviewQueueIndex,
      },
    });
  }, [data, id, navigate, resolvedPatientId, mergedMultiPatient, typedMultiQueue]);

  const handleSaveCarePlan = useCallback(
    (payload) => {
      if (!payload?.patient_id || !payload?.note_content) return;
      saveCarePlan(payload.patient_id, payload.note_content, {
        documentationTime: payload.documentationTime,
        care_time: payload.care_time,
        care_day_offset: payload.care_day_offset,
      });
    },
    [saveCarePlan]
  );

  const handleSaveNote = useCallback(
    (note) => {
      const patientIdForSave = note.patient_id || resolvedPatientId;
      saveNote({
        ...note,
        patient_id: patientIdForSave,
        recordingDurationSeconds:
          note.recordingDurationSeconds || data?.recordingDurationSeconds || null,
        manualTaskCompletionTaskId:
          reviewPayload?.manualTaskCompletionTaskId || note.manualTaskCompletionTaskId,
      });
    },
    [reviewPayload?.manualTaskCompletionTaskId, resolvedPatientId, saveNote]
  );

  const handleDiscard = useCallback(async () => {
    const confirmed = window.confirm(
      language === 'ar'
        ? 'هل أنت متأكد أنك تريد تجاهل هذه الملاحظة؟'
        : 'Are you sure you want to discard this note?'
    );
    if (!confirmed) return;
    if (data?.clinicalIds?.noteId) {
      await discardClinicalNote(data.clinicalIds);
    }
    setDraftGeneration(null);
    navigate(id ? patientPath(id) : ROUTES.PATIENTS);
  }, [data?.clinicalIds, id, language, navigate, setDraftGeneration]);

  const handleSaveComplete = useCallback(() => {
    setDraftGeneration(null);
    navigate(id ? patientPath(id) : ROUTES.PATIENTS);
  }, [id, navigate, setDraftGeneration]);

  if (!data?.noteType && !mergedMultiPatient && !typedMultiQueue) {
    return (
      <ReviewModal onClose={() => navigate(ROUTES.PATIENTS)}>
        <div className="p-8 text-center text-gray-500">No note to review.</div>
      </ReviewModal>
    );
  }

  if (!id && !resolvedPatientId && !isMulti) {
    return (
      <ReviewModal onClose={() => navigate(ROUTES.PATIENTS)}>
        <div className="p-8 text-center text-gray-500">Select a patient to continue.</div>
      </ReviewModal>
    );
  }

  if (typedMultiQueue) {
    return (
      <ReviewModal onClose={() => navigate(ROUTES.PATIENTS)}>
        <MultiPatientQueueReview
          key={typedMultiQueue.map((item) => item.noteId || item.encounterGroupId).join('-')}
          queue={typedMultiQueue}
          onSaveNote={handleSaveNote}
          onDiscard={handleDiscard}
          onSaveComplete={handleSaveComplete}
        />
      </ReviewModal>
    );
  }

  return (
    <ReviewModal onClose={() => navigate(ROUTES.PATIENTS)}>
      <NoteReview
        key={reviewPayload.encounterGroupId || reviewPayload.noteId || 'review'}
        noteId={reviewPayload.noteId}
        encounterGroupId={reviewPayload.encounterGroupId}
        patientId={resolvedPatientId}
        encounterDateTime={new Date(reviewPayload.encounterDateTime || Date.now())}
        transcript={reviewPayload.transcriptCleaned || reviewPayload.transcriptRaw}
        transcriptRaw={reviewPayload.transcriptRaw}
        transcriptCleaned={reviewPayload.transcriptCleaned}
        noteType={reviewPayload.noteType}
        generatedNotes={reviewPayload.generatedNotes}
        openaiNote={reviewPayload.openaiNote}
        companionVitalsNote={reviewPayload.companionVitalsNote}
        cortiNote={reviewPayload.cortiNote}
        cortiRaw={reviewPayload.cortiRaw}
        cortiFailed={reviewPayload.cortiFailed}
        openaiError={reviewPayload.openaiError}
        manualTaskCompletionType={reviewPayload.manualTaskCompletionType}
        onSaveNote={handleSaveNote}
        onSaveCarePlan={handleSaveCarePlan}
        onDiscard={handleDiscard}
        onSaveComplete={handleSaveComplete}
        clinicalIds={reviewPayload.clinicalIds || null}
        recordingDurationSeconds={reviewPayload.recordingDurationSeconds || null}
      />
    </ReviewModal>
  );
}
